import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BankAccountsService } from '../../../../services/financial-accounts/bank-accounts.service';
import { ManualAccountEntryComponent, ManualEntryDialogInput, ManualEntryDialogResult } from './manual-account-entry/manual-account-entry.component';
import { Observable, Subject } from 'rxjs';
import { PaymentBankAccount } from '../../../../models/entities/payment-bank-account';
import { UnifiedBankAccount } from '../../../../models/entities/unified-bank-account';
import { HttpErrorResponse } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { GrowlerService } from '../../../../services/growler.service';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { PlaidLinkComponent, PlaidLinkResult } from './plaid-link/plaid-link.component';
import { PlaidService } from '../../../../services/plaid.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DeleteBankAccountDialogComponent, DeleteBankAccountDialogInput } from './delete-bank-account-dialog/delete-bank-account-dialog.component';
import { RemoveDepositAccountDialogComponent, RemoveDepositAccountDialogInput, RemoveDepositAccountDialogResult } from './remove-deposit-account-dialog/remove-deposit-account-dialog.component';
import { LastDepositAccountErrorDialogComponent, LastDepositAccountErrorDialogInput } from './last-deposit-account-error-dialog/last-deposit-account-error-dialog.component';
import { PlaidSearchComponent, PlaidSearchDialogInput, PlaidSearchDialogResult } from './plaid-search/plaid-search.component';
import { BankAccountUsageDialogComponent, BankAccountUsageDialogForm, BankAccountUsageDialogInput, BankAccountUsageDialogResult } from './bank-account-usage-dialog/bank-account-usage-dialog.component';
import { MicrodepositVerificationComponent, MicrodepositVerificationDialogInput, MicrodepositVerificationDialogResult } from './microdeposit-verification/microdeposit-verification.component';
import { BankAccountPropertiesComponent, BankAccountPropertiesDialogInput, BankAccountPropertiesDialogResult } from './bank-account-properties/bank-account-properties.component';
import { RemovePaymentAccountDialogComponent, RemovePaymentAccountDialogInput, RemovePaymentAccountDialogResult } from './remove-payment-account-dialog/remove-payment-account-dialog.component';
import { LoadingDialogComponent } from '../loading-dialog/loading-dialog.component';
import { LinkedBankAccount } from '../../../../models/entities/linked-bank-account';
import { PaymentMethodsService } from '../../../../services/financial-accounts/payment-methods.service';
import { LobBankAccount } from '../../../../models/entities/lob-bank-account';
import { PlaidTokenDetailDialogComponent, PlaidTokenDetailDialogInput } from './plaid-token-detail-dialog/plaid-token-detail-dialog.component';
import { GenericMatDialogComponent, GenericMatDialogInput } from '../generic-mat-dialog/generic-mat-dialog.component';
import { GenericDialogResult } from '../generic-mat-dialog/generic-dialog-result';
import { UnlinkBankAccountDialogComponent, UnlinkBankAccountDialogInput } from './unlink-bank-account-dialog/unlink-bank-account-dialog.component';
import { ToggleTransactionsDialogComponent } from './toggle-transactions-dialog/toggle-transactions-dialog.component';
import { ConfirmActionModalComponent, ConfirmActionModalInput } from '../confirm-action-modal/confirm-action-modal.component';
import { AttentionFlag } from '../../../../models/entities/attention-flag';

@UntilDestroy()
@Component({
    selector: 'lf-bank-account-manager',
    templateUrl: './bank-account-manager.component.html',
})
export class BankAccountManagerComponent implements OnInit
{
    fixable = [
        'INVALID_CREDENTIALS',
        'INVALID_MFA',
        'ITEM_LOGIN_REQUIRED',
    ];

    informationMessages = {
        ITEM_LOCKED: {
            title: 'Your account is locked.',
            text: 'Please contact your bank to unlock your account.',
        },
        USER_SETUP_REQUIRED: {
            title: 'User Setup is required',
            text:
                    'You need to take action on your bank website to use this feature.',
        },
        PRODUCT_NOT_READY: {
            title: 'We are still pulling your transactions.',
            text: 'Please try again a little later.',
        },
        INSTITUTION_DOWN: {
            title: 'Your bank is currently not responding.',
            text: 'Please try again a little later.',
        },
        INSTITUTION_NOT_RESPONDING: {
            title: 'Your bank is currently not responding.',
            text: 'Please try again a little later.',
        },
        INSTITUTION_NOT_AVAILABLE: {
            title: 'Your bank is currently not responding.',
            text: 'Please try again a little later.',
        },
        TRANSACTIONS_UPDATES_DEGRADED: {
            title: 'Transactions may not update.',
            text: 'A large number of transaction requests to your bank are failing. Transaction imports may be delayed as a result.',
        },
    };

    noSupportMessages = {
        ITEM_NOT_SUPPORTED: {
            title: 'Your account is no longer supported.',
            text:
                    'We will no longer be able to pull your account ledger from your bank.',
        },
        MFA_NOT_SUPPORTED: {
            title:
                    'Your bank requires a verification step we currently do not support.',
            text:
                    'We will no longer be able to pull your account ledger from your bank.',
        },
        NO_ACCOUNTS: {
            title:
                    'Your bank is reporting your account is no longer available to our system.',
            text:
                    'We will no longer be able to pull your account ledger from your bank.',
        },
        INSTITUTION_NO_LONGER_SUPPORTED: {
            title: 'Support for you bank has been removed.',
            text:
                    'We will no longer be able to pull your account ledger from your bank.',
        },
    };

    @Input() userId: number = null;
    @Input() context: string = null;
    @Output() unifiedBankAccountSaved: EventEmitter<UnifiedBankAccount> = new EventEmitter();
    @Output() paymentBankAccountSaved: EventEmitter<PaymentBankAccount> = new EventEmitter();
    @Output() bankAccountsLoaded: Subject<any> = new Subject();
    @ViewChild('plaidLink') plaidLink: PlaidLinkComponent;
    isLoadingAccounts: boolean = false;
    unifiedBankAccounts: UnifiedBankAccount[] = [];
    paymentBankAccounts: PaymentBankAccount[] = [];
    accountToBeLinked: UnifiedBankAccount = null;
    jwt: JwtLegFiClaims;
    private _loadingDialog: MatDialogRef<LoadingDialogComponent> = null;

    constructor(
            private _bankAccountsService: BankAccountsService,
            private _growlerService: GrowlerService,
            private _paymentMethodsService: PaymentMethodsService,
            private _plaidService: PlaidService,
            private _matDialogService: MatDialog,
    ) {
    }

    get hasOrganizationBankAccounts() {
        return this.unifiedBankAccounts.length > 0;
    }

    get organizationBankAccounts() {
        return this.unifiedBankAccounts;
    }

    get organizationDepositAccounts() {
        return this.unifiedBankAccounts.filter((uba: UnifiedBankAccount) => {
            return uba.depositBankAccount;
        });
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();

        if (!this.context || [
            'organization',
            'user',
            'billpay',
        ].indexOf(this.context) === -1) {
            console.error('[BankAccountManager] Unknown context ' + this.context);
        }
        this.loadBankAccounts();
    }

    loadBankAccounts() {
        this.isLoadingAccounts = true;
        if (this.context === 'organization') {
            this._bankAccountsService
                    .getOrgBankAccounts(null, {})
                    .pipe(untilDestroyed(this))
                    .subscribe((unifiedBankAccounts: UnifiedBankAccount[]) => {
                        this._closeLoadingDialog();
                        this.isLoadingAccounts = false;
                        this.unifiedBankAccounts = unifiedBankAccounts;
                        this.bankAccountsLoaded.next(true);
                    }, () => {
                        this._closeLoadingDialog();
                        this.isLoadingAccounts = false;
                    });
        } else if (this.context === 'user') {
            this._bankAccountsService
                    .getUserBankAccounts(this.userId)
                    .pipe(untilDestroyed(this))
                    .subscribe(
                            (paymentBankAccounts: PaymentBankAccount[]) => {
                                this._closeLoadingDialog();
                                this.isLoadingAccounts = false;
                                this.paymentBankAccounts = paymentBankAccounts;
                                this.bankAccountsLoaded.next(true);
                            },
                            () => {
                                this._closeLoadingDialog();
                                this.isLoadingAccounts = false;
                            },
                    );
        }
    }

    async createFixedAsset(acct) {
        this._matDialogService.open(ConfirmActionModalComponent, {
            data: <ConfirmActionModalInput>{
                title: 'Create Fixed Asset',
                content: 'Create a fixed asset for this bank account? This causes the account to show on the transactions ledger.',
                confirmButton: 'Create',
            },
        }).afterClosed().subscribe({
            next: (result: GenericDialogResult) => {
                if (result && result.success) {
                    this._bankAccountsService.createFixedAssetForAccount(acct.id)
                            .subscribe({
                                next: () => {
                                    this._growlerService.success('Fixed Asset Created', 'A fixed asset has been created for the selected account');
                                    this.loadBankAccounts();
                                },
                                error: () => {
                                    this._growlerService.error('Fixed Asset Failed', 'A fixed asset has could not be created for the selected account');
                                },
                            });
                }
            },
        });
    }

    async bankAccountProperties(acct) {
        this._matDialogService.open(BankAccountPropertiesComponent, {
            width: '555px',
            data: <BankAccountPropertiesDialogInput>{bankAccount: acct},
        }).afterClosed().pipe(untilDestroyed(this)).subscribe(
                (result: BankAccountPropertiesDialogResult) => {
                    if (result.success) {
                        this._showLoadingDialog();
                        this.loadBankAccounts();
                    }
                },
        );
    }

    async deleteBankAccount(bankAccount: UnifiedBankAccount | PaymentBankAccount) {
        if (this.context === 'organization' && bankAccount instanceof UnifiedBankAccount) {
            const replacementCandidates: UnifiedBankAccount[] = this._getReplacementAccountCandidates(bankAccount);
            if (bankAccount.depositBankAccount && !(replacementCandidates.length)) {
                this._matDialogService.open(LastDepositAccountErrorDialogComponent, {
                    data: <LastDepositAccountErrorDialogInput>{
                        account: bankAccount,
                    },
                    width: '555px',
                });
                return;
            }

            this._matDialogService.open(DeleteBankAccountDialogComponent, {
                width: '600px',
                data: <DeleteBankAccountDialogInput>{
                    account: bankAccount,
                    replacementCandidates: replacementCandidates,
                },
            }).afterClosed().pipe(untilDestroyed(this)).subscribe(
                    (status: GenericDialogResult) => {
                        if (status.success) {
                            this._showLoadingDialog();
                            this.loadBankAccounts();
                        }
                    },
            );
        } else if (this.context === 'user' && bankAccount instanceof PaymentBankAccount) {
            this._matDialogService.open(DeleteBankAccountDialogComponent, {
                width: '600px',
                data: <DeleteBankAccountDialogInput>{
                    account: bankAccount,
                },
            }).afterClosed().pipe(untilDestroyed(this)).subscribe(
                    (status: GenericDialogResult) => {
                        if (status.success) {
                            this._showLoadingDialog();
                            this.loadBankAccounts();
                        }
                    },
            );
        }
    }

    async addBankAccount(requirePayment: boolean = false, requireDeposit: boolean = false) {
        if (requirePayment && requireDeposit) {
            requirePayment = false;
            requireDeposit = false;
        }
        // Require the first bank account added to be set as a payment method
        if (this.unifiedBankAccounts.length === 0) {
            requirePayment = true;
        }
        const usageConfig = <BankAccountUsageDialogInput>{
            dialogTitle: 'New Account Settings',
            accountName: 'Checking Account - ',
            confirmButtonText: this.accountToBeLinked ? 'Link Bank Account' : 'Add Bank Account',
            context: this.context,
            promptUseForTransactions: true,
            disablePayments: requirePayment,
            disableDeposits: requireDeposit,
            supportsTransactions: false,
            promptAccountNumbers: false,
        };
        const searchResult: PlaidSearchDialogResult = await this._showPlaidSearch(this.accountToBeLinked !== null);
        if (searchResult && searchResult.success) {
            if (searchResult.selection) {
                if (searchResult.selection.products.indexOf('auth') === -1) {
                    usageConfig.promptAccountNumbers = true;
                }
                if (searchResult.selection.products.indexOf('transactions') !== -1) {
                    usageConfig.supportsTransactions = true;
                }
                const plaidLinkResult: PlaidLinkResult = await this.plaidLink.linkNewAccount(this.context, searchResult.selection.institutionId);
                if (plaidLinkResult.success) {
                    usageConfig.accountName = plaidLinkResult.metadata.account.name + ' - ' + plaidLinkResult.metadata.account.mask;
                    if (this.accountToBeLinked !== null) {
                        usageConfig.accountName = this.accountToBeLinked.friendlyName;
                    }
                    await this._usageLoop(usageConfig, plaidLinkResult, requirePayment, requireDeposit);
                } else {
                    // plaid link was exited, restart
                    return await this.addBankAccount(requirePayment, requireDeposit);
                }
            } else {
                return await this._handleManualAdd(usageConfig);
            }
        }
        // Search cancelled
        this.accountToBeLinked = null;
    }

    addDepositAccount() {
        return this.addBankAccount(false, true);
    }

    addPaymentAccount() {
        return this.addBankAccount(true, false);
    }

    beginManualMicrodepositVerification(forAccount: PaymentBankAccount) {
        return new Promise((resolve, reject) => {
            this._matDialogService.open(MicrodepositVerificationComponent, {
                data: <MicrodepositVerificationDialogInput>{paymentBankAccount: forAccount},
                width: '700px',
            })
                    .afterClosed().pipe(untilDestroyed(this))
                    .subscribe(
                            (result: MicrodepositVerificationDialogResult) => {
                                if (result.success) {
                                    forAccount.verifiedMicrodeposits = true;
                                    this._showLoadingDialog();
                                    this.loadBankAccounts();
                                    return resolve(forAccount);
                                }
                                reject();
                            },
                            () => {
                                reject();
                            },
                    );
        });
    }

    beginLobMicrodepositVerification(forAccount: LobBankAccount) {
        // this.showMicrodepositVerificationModal = true;
        return new Promise((resolve, reject) => {
            this._matDialogService.open(MicrodepositVerificationComponent, {
                data: <MicrodepositVerificationDialogInput>{lobBankAccount: forAccount},
                width: '700px',
            })
                    .afterClosed().pipe(untilDestroyed(this))
                    .subscribe(
                            (result: MicrodepositVerificationDialogResult) => {
                                if (result.success) {
                                    return resolve(true);
                                }
                                reject();
                            },
                    );
        });
    }

    /**
     *
     * @param unifiedBankAccount
     * @param type
     */
    upgradeWithStripeIntegrations(unifiedBankAccount: UnifiedBankAccount, type: string) {
        this._confirmDetails(unifiedBankAccount, (details) => {
            this._showLoadingDialog();
            if (type === 'deposit') {
                this._bankAccountsService.createManagedAccountFromOrgBank(null, unifiedBankAccount.id, details).subscribe(
                        () => {
                            this.loadBankAccounts();
                            this._growlerService.success('Success', 'We have successfully added this bank account as a deposit destination.');
                        },
                        (response: HttpErrorResponse) => {
                            this._growlerService.error(
                                    'Problems updating your account.',
                                    response.error['errors'][0]['msg'],
                            );
                        },
                );
            } else if (type === 'payment') {
                this._bankAccountsService.attachPaymentMethodToOrgBank(null, unifiedBankAccount.id, details).subscribe(
                        () => {
                            this.loadBankAccounts();
                            this._growlerService.success('Success', 'We have successfully added this bank account as a payment method.');
                        },
                        (response: HttpErrorResponse) => {
                            this._growlerService.error(
                                    'Problems updating your account.',
                                    response.error['errors'][0]['msg'],
                            );
                        },
                );
            }
        }, () => {
            // Do nothing
        });
    }

    deleteDepositAccount(unifiedBankAccount: UnifiedBankAccount) {
        if (!unifiedBankAccount.depositBankAccount) {
            return;
        }

        const replacementCandidates: UnifiedBankAccount[] = this._getReplacementAccountCandidates(unifiedBankAccount);
        if (unifiedBankAccount.depositBankAccount && !(replacementCandidates.length)) {
            this._matDialogService.open(LastDepositAccountErrorDialogComponent, {
                data: <LastDepositAccountErrorDialogInput>{
                    account: unifiedBankAccount,
                },
                width: '555px',
            });
            return;
        }

        this._matDialogService.open(RemoveDepositAccountDialogComponent, {
            data: <RemoveDepositAccountDialogInput>{
                account: unifiedBankAccount,
                replacementCandidates: replacementCandidates,
            },
            width: '700px',
        }).afterClosed().pipe(untilDestroyed(this)).subscribe(
                (result: RemoveDepositAccountDialogResult) => {
                    if (result.success) {
                        this._showLoadingDialog();
                        this.loadBankAccounts();
                    }
                },
        );
    }

    /**
     *
     * @param unifiedBankAccount
     */
    deletePaymentAccount(unifiedBankAccount: UnifiedBankAccount) {
        if (!unifiedBankAccount.paymentBankAccount) {
            return;
        }

        this._matDialogService.open(RemovePaymentAccountDialogComponent, {
            width: '550px',
            data: <RemovePaymentAccountDialogInput>{
                account: unifiedBankAccount,
            },
        }).afterClosed().pipe(untilDestroyed(this)).subscribe(
                (result: RemovePaymentAccountDialogResult) => {
                    if (result.success) {
                        this._showLoadingDialog();
                        this.loadBankAccounts();
                    }
                },
        );
    }

    async toggleTransactions(unifiedBankAccount: UnifiedBankAccount) {
        const result: GenericDialogResult = await this._matDialogService.open(ToggleTransactionsDialogComponent, {
            width: '555px',
            data: <UnlinkBankAccountDialogInput>{
                account: unifiedBankAccount,
            },
        }).afterClosed().toPromise();

        if (result.success) {
            this._showLoadingDialog();
            this.loadBankAccounts();
        }
        //
        // this._plaidService.toggleTransactions(unifiedBankAccount.linkedBankAccount.id, true)
        //         .subscribe(() =>
        //         {
        //             this._showLoadingDialog();
        //             this.loadBankAccounts();
        //         });
    }

    linkAccount(unifiedBankAccount: UnifiedBankAccount) {
        this.accountToBeLinked = unifiedBankAccount;
        // Go through add workflow, require types that this account already is
        this.addBankAccount(unifiedBankAccount.paymentBankAccount !== null, unifiedBankAccount.depositBankAccount !== null);
    }

    async unlinkAccount(bankAccount: UnifiedBankAccount) {
        const result: GenericDialogResult = await this._matDialogService.open(UnlinkBankAccountDialogComponent, {
            width: '555px',
            data: <UnlinkBankAccountDialogInput>{
                account: bankAccount,
                manager: this,
            },
        }).afterClosed().toPromise();

        if (result.success) {
            this.loadBankAccounts();
        }
    }

    repairConnectionForUserAccount(paymentBankAccount: PaymentBankAccount) {
        this._showLoadingDialog();
        this.plaidLink.repairLinkForUser(paymentBankAccount)
                .then(() => {
                    this.refreshLinkedPaymentAccount(paymentBankAccount);
                }).catch(() => {
            this.refreshLinkedPaymentAccount(paymentBankAccount);
        });
    }

    repairConnection(unifiedBankAccount: UnifiedBankAccount) {
        this._showLoadingDialog();
        this.plaidLink.repairLinkForOrg(unifiedBankAccount.linkedBankAccount)
                .then(() => {
                    this.refreshLinkedAccount(unifiedBankAccount);
                }).catch(() => {
            this.refreshLinkedAccount(unifiedBankAccount);
        });
    }

    getAttentionReasonText(attentionFlag: AttentionFlag) {
        if (this.fixable.indexOf(attentionFlag.errorCode) !== -1) {
            return 'We are unable to pull transaction information from this linked bank account. This can occur if your online banking credentials change. Please click to repair the connection.';
        }
        if (this.informationMessages.hasOwnProperty(attentionFlag.errorCode)) {
            return this.informationMessages[attentionFlag.errorCode].title + ' ' + this.informationMessages[attentionFlag.errorCode].text;
        }
        if (this.noSupportMessages.hasOwnProperty(attentionFlag.errorCode)) {
            return this.noSupportMessages[attentionFlag.errorCode].title + ' ' + this.noSupportMessages[attentionFlag.errorCode].text;
        }
    }

    handleAttentionReason(account: LinkedBankAccount | PaymentBankAccount) {
        const flag = account.attentionFlags[0];

        if (flag.errorType === 'relink') {
            if (this.context === 'organization') {
                return this.repairConnection(account.unifiedBankAccount);
            } else if (this.context === 'user' && account instanceof PaymentBankAccount) {
                return this.repairConnectionForUserAccount(account);
            }
        }

        if (
                flag.errorType === 'ITEM_ERROR' ||
                flag.errorType === 'INSTITUTION_ERROR'
        ) {
            if (this.fixable.indexOf(flag.errorCode) !== -1) {
                if (this.context === 'organization') {
                    return this.repairConnection(account.unifiedBankAccount);
                } else if (this.context === 'user' && account instanceof PaymentBankAccount) {
                    return this.repairConnectionForUserAccount(account);
                }
            }

            let title = null;
            let text = null;
            if (this.informationMessages.hasOwnProperty(flag.errorCode)) {
                title = this.informationMessages[flag.errorCode].title;
                text = this.informationMessages[flag.errorCode].text;
            }

            if (this.noSupportMessages.hasOwnProperty(flag.errorCode)) {
                title = this.noSupportMessages[flag.errorCode].title;
                text = this.noSupportMessages[flag.errorCode].text;
            }

            if (title && text) {
                this._matDialogService.open(GenericMatDialogComponent, {
                    data: <GenericMatDialogInput>{title: title, text: text},
                });
            }
        }
    }

    /**
     *
     * @param account
     */
    refreshLinkedAccount(account: UnifiedBankAccount) {
        this._showLoadingDialog();
        this._plaidService.refreshLinkedAccount(true, account.linkedBankAccount.id)
                .subscribe(
                        () => {
                            this.loadBankAccounts();
                        },
                );
    }

    refreshDepositStripeFields(account: UnifiedBankAccount) {
        this._showLoadingDialog();
        this._bankAccountsService.refreshDepositStripeFields(account.depositBankAccount.id)
                .subscribe(
                        () => {
                            this.loadBankAccounts();
                        },
                );
    }

    /**
     *
     * @param account
     */
    refreshLinkedPaymentAccount(account: PaymentBankAccount) {
        this._showLoadingDialog();
        this._plaidService.refreshLinkedAccount(false, account.id)
                .subscribe(
                        () => {
                            this.loadBankAccounts();
                        },
                );
    }

    reenrollPaymentAccount(account: PaymentBankAccount | UnifiedBankAccount) {
        if (account instanceof PaymentBankAccount) {
            this._bankAccountsService.reenrollUserPaymentAccount(account)
                    .subscribe(
                            () => {

                            },
                    );
        } else {
            this._bankAccountsService.reenrollOrganizationPaymentAccount(account)
                    .subscribe(
                            () => {

                            },
                    );
        }
    }

    reenrollDepositAccount(account: UnifiedBankAccount) {
        this._bankAccountsService.reenrollDepositAccount(account)
                .subscribe(
                        () => {

                        },
                );
    }

    /**
     *
     * @param account
     */
    showPlaidToken(account: UnifiedBankAccount | PaymentBankAccount) {
        if (account instanceof UnifiedBankAccount) {
            this._matDialogService.open(PlaidTokenDetailDialogComponent, {
                data: <PlaidTokenDetailDialogInput>{
                    friendlyName: account.friendlyName,
                    plaidToken: account.linkedBankAccount.plaidAccessToken,
                    plaidItemId: account.linkedBankAccount.plaidItemId,
                },
            });
        } else if (account instanceof PaymentBankAccount) {
            this._matDialogService.open(PlaidTokenDetailDialogComponent, {
                data: <PlaidTokenDetailDialogInput>{
                    friendlyName: account.friendlyName,
                    plaidToken: account.plaidAccessToken,
                    plaidItemId: account.plaidItemId,
                },
            });
        }
    }

    private async _handleManualAdd(usageConfig, routingNumber = null, accountNumber = null, message = null) {
        const manualAddInfo = await this._showManualEntry(routingNumber, accountNumber, message);
        usageConfig.accountName = 'Checking Account - ' + manualAddInfo.accountNumber.toString().slice(-4);
        usageConfig.promptUseForTransactions = false;
        if (manualAddInfo.success) {
            const usageDialogResult: BankAccountUsageDialogResult = await this._showUsageModal(usageConfig);
            if (usageDialogResult.success) {
                this._showLoadingDialog();
                try {
                    if (this.context === 'user') {
                        await this._importBankManuallyForUser(manualAddInfo, usageDialogResult.usage);
                    } else {
                        const unifiedBankAccount: UnifiedBankAccount = await this._importBankManuallyForOrg(manualAddInfo, usageDialogResult.usage);
                        this.unifiedBankAccountSaved.emit(unifiedBankAccount);
                    }
                    this._showLoadingDialog();
                    this.loadBankAccounts();
                    this._growlerService.success('Success', 'We have successfully added the bank to your account.');
                } catch (e) {
                    const response: HttpErrorResponse = e;
                    const errorMessage = response.error['errors'][0]['msg'];
                    this._growlerService.error(
                            'Problems adding your account.',
                            'We were unable to enroll your bank account. See below for additional information.',
                    );

                    this._closeLoadingDialog();
                    return await this._handleManualAdd(usageConfig, manualAddInfo.routingNumber, manualAddInfo.accountNumber, errorMessage);
                }
            } else {
                // usage dialog exited without completion, restart
                return await this.addBankAccount(usageConfig.disablePayments, usageConfig.disableDeposits);
            }
        } else {
            // manual add exited without completion, restart
            return await this.addBankAccount(usageConfig.disablePayments, usageConfig.disableDeposits);
        }
    }

    private async _usageLoop(usageConfig, plaidLinkResult, requirePayment, requireDeposit) {
        this._closeLoadingDialog();
        const usageDialogResult: BankAccountUsageDialogResult = await this._showUsageModal(usageConfig);
        if (usageDialogResult.success) {
            this._showLoadingDialog();
            if (this.context === 'user') {
                const result = await this._retrieveAccountForUser(plaidLinkResult, usageDialogResult.usage);
                if (!result) {
                    return await this._usageLoop(usageConfig, plaidLinkResult, requirePayment, requireDeposit);
                }
            } else {
                const result = await this._retrieveAccountForOrg(plaidLinkResult, usageDialogResult.usage);
                if (!result) {
                    return await this._usageLoop(usageConfig, plaidLinkResult, requirePayment, requireDeposit);
                }
            }
        } else {
            // usage dialog exited without completion, restart
            return await this.addBankAccount(requirePayment, requireDeposit);
        }
    }

    private async _importBankManuallyForOrg(
            bankInfo: ManualEntryDialogResult,
            usageDialogForm: BankAccountUsageDialogForm,
    ) {
        const body = {
            account: bankInfo.accountNumber.toString(),
            routing: bankInfo.routingNumber.toString(),
            accountName: usageDialogForm.accountName,
            deposits: usageDialogForm.useForDeposits,
            payments: usageDialogForm.useForPayments,
            captcha: usageDialogForm.captchaToken,
        };

        return await this._bankAccountsService.createBankForOrgFromBankInfo(null, body).toPromise();
    }

    private async _importBankManuallyForUser(
            bankInfo: ManualEntryDialogResult,
            usageDialogForm: BankAccountUsageDialogForm,
    ) {
        const body = {
            account: bankInfo.accountNumber.toString(),
            routing: bankInfo.routingNumber.toString(),
            accountName: usageDialogForm.accountName,
            bankAccountId: null,
            captcha: usageDialogForm.captchaToken,
        };
        return await this._bankAccountsService.createBankForUserFromBankInfo(this.userId, body).toPromise();
    }

    private _showManualEntry(
            routingNumber = null,
            accountNumber = null,
            message: string = null,
    ): Promise<ManualEntryDialogResult> {
        return this._matDialogService.open(ManualAccountEntryComponent, {
            width: '555px',
            data: <ManualEntryDialogInput>{
                dialogTitle: 'Please Provide Your Account Details',
                dialogError: message,
                routingNumber: routingNumber,
                accountNumber: accountNumber,
            },
        }).afterClosed().pipe(untilDestroyed(this)).toPromise();
    }

    private _showUsageModal(input: BankAccountUsageDialogInput): Promise<BankAccountUsageDialogResult> {
        return this._matDialogService.open(BankAccountUsageDialogComponent, {
            data: <BankAccountUsageDialogInput>input,
            disableClose: true,
        }).afterClosed().pipe(untilDestroyed(this)).toPromise();
    }

    private async _retrieveAccountForOrg($event: PlaidLinkResult, usageDialogForm: BankAccountUsageDialogForm) {
        let request: Observable<UnifiedBankAccount> = null;
        if (this.accountToBeLinked === null) {
            const requestBody = {
                publicToken: $event.publicToken,
                accountId: $event.metadata.account_id,
                accountName: usageDialogForm.accountName,
                transactions: usageDialogForm.useForTransactions,
                deposits: usageDialogForm.useForDeposits,
                payments: usageDialogForm.useForPayments,
                captcha: usageDialogForm.captchaToken,
                routingNumber: usageDialogForm.routingNumber,
                accountNumber: usageDialogForm.accountNumber,
            };
            request = this._bankAccountsService.createBankForOrgFromPlaidAuth(null, requestBody);
        } else {
            const requestBody = {
                publicToken: $event.publicToken,
                accountId: $event.metadata.account_id,
                accountName: usageDialogForm.accountName,
                transactions: usageDialogForm.useForTransactions,
                deposits: usageDialogForm.useForDeposits,
                payments: usageDialogForm.useForPayments,
                captcha: usageDialogForm.captchaToken,
            };

            request = this._bankAccountsService.linkExistingBankAccount(null, this.accountToBeLinked.id, requestBody);
        }

        try {
            const unifiedBankAccount: UnifiedBankAccount = await request.toPromise();
            this._showLoadingDialog();
            this.loadBankAccounts();
            this.accountToBeLinked = null;
            this._growlerService.success('Success', 'We have successfully added your bank account.');
            this.unifiedBankAccountSaved.emit(unifiedBankAccount);
            return true;
        } catch (err) {
            if (err.error) {
                const errorResponse = <HttpErrorResponse>err;
                const log = errorResponse.error.errors[0].msg;
                this.accountToBeLinked = null;
                this._closeLoadingDialog();
                this._growlerService.error('Error Completing Request', log);
                return false;
            } else {
                this._growlerService.error('Error Completing Request', 'An unknown error was encountered while attempting to add your bank account. Please try again.');
                return false;
            }
        }
    }

    private async _retrieveAccountForUser($event: PlaidLinkResult, usageDialogForm: BankAccountUsageDialogForm) {
        const requestBody = {
            publicToken: $event.publicToken,
            accountId: $event.metadata.account_id,
            accountName: usageDialogForm.accountName,
            captcha: usageDialogForm.captchaToken,
        };

        try {
            const bankAccount: PaymentBankAccount = await this._bankAccountsService.createBankForUserFromPlaidAuth(this.userId, requestBody)
                    .toPromise();

            this._showLoadingDialog();
            this.loadBankAccounts();
            this.paymentBankAccountSaved.emit(bankAccount);
            this._growlerService.success('Success', 'We have successfully added your bank account.');
            return true;
        } catch (err) {
            if (err.error) {
                const errorResponse = <HttpErrorResponse>err;
                const log = errorResponse.error.errors[0].msg;
                this._growlerService.error('Error Completing Request', log);
                return false;
            } else {
                this._growlerService.error('Error Completing Request', 'An unknown error was encountered while attempting to add your bank account. Please try again.');
                return false;
            }
        }
    }

    /**
     * Get all unified bank accounts that are setup as a deposit destination
     */
    private _getDepositAccounts(): UnifiedBankAccount[] {
        return this.unifiedBankAccounts
                .filter(bank => bank.depositBankAccount);
    }

    /**
     * Get all unified bank accounts that are setup as a deposit destination,
     * that are not the same account as the one passed in to use as a replacement list.
     * @param bankAccountToDelete
     * @private
     */
    private _getReplacementAccountCandidates(bankAccountToDelete: UnifiedBankAccount): UnifiedBankAccount[] {
        return this._getDepositAccounts()
                .filter(bank => bank.id !== bankAccountToDelete.id);
    }

    private _confirmDetails(initialAccount: UnifiedBankAccount, callbackSuccess, callbackCancelled) {
        this._matDialogService.open(ManualAccountEntryComponent, {
            data: <ManualEntryDialogInput>{
                dialogTitle: 'Please Confirm the Details for ' + initialAccount.friendlyName,
            },
            width: '555px',
        })
                .afterClosed().pipe(untilDestroyed(this))
                .subscribe((result: ManualEntryDialogResult) => {
                    if (result.success) {
                        if (callbackSuccess) {
                            callbackSuccess(result);
                        }
                    } else {
                        if (callbackCancelled) {
                            callbackCancelled();
                        }
                    }
                });
    }

    private _showPlaidSearch(requireLink: boolean = false): Promise<PlaidSearchDialogResult> {
        return this._matDialogService.open(PlaidSearchComponent, {
            panelClass: 'min-30',
            data: <PlaidSearchDialogInput>{
                requireLink: requireLink,
                context: this.context,
            },
        })
                .afterClosed().pipe(untilDestroyed(this))
                .toPromise();
    }

    /**
     * Show the loading dialog
     * @private
     */
    private _showLoadingDialog() {
        if (!this._loadingDialog) {
            this._loadingDialog = this._matDialogService.open(LoadingDialogComponent, {
                data: {title: 'Refreshing Accounts...'},
                disableClose: true,
                width: '555px',
            });
        }
    }

    /**
     * Close the loading dialog
     * @private
     */
    private _closeLoadingDialog() {
        if (this._loadingDialog) {
            this._loadingDialog.close();
            this._loadingDialog = null;
        }
    }
}
